import {
  Button,
  ChevronDownIcon,
  cn,
  MicrophoneIcon,
  MicrophoneSlashIcon,
  PhoneIcon,
  PhoneSlashIcon,
  VideoIcon,
  VideoSlashIcon,
} from '@quickmd-llc/design-system'
import { useEffect, useMemo, useState } from 'react'
import { useDailyVisit } from '../hooks/useDailyVisit'
import moment from 'moment'

export function DailyFooter() {
  const {
    is,
    endVisitCall,
    isVideoCall,
    visitIsPhoneCallOnly,
    participantConnectionStatuses,
    onStartVisitClickInVisit,
    preferredAudioOn,
    preferredVideoOn,
    setPreferredAudioOn,
    setPreferredVideoOn,
    isPrepMode,
    patientWithVisitInfo,
  } = useDailyVisit()

  const [showCallActions, setShowCallActions] = useState<boolean>(false)
  const { VisitMethod } = patientWithVisitInfo

  const toggleCam = () => {
    setPreferredVideoOn(!preferredVideoOn)
  }

  const toggleMic = () => {
    setPreferredAudioOn(!preferredAudioOn)
  }

  const onStartCall = () => {
    onStartVisitClickInVisit({ isPhoneCall: VisitMethod !== 'video' })
  }

  const onCallAgain = () => {
    if (patientWithVisitInfo.VisitMethod !== 'video') {
      onStartVisitClickInVisit({ isPhoneCall: true })
    } else {
      setShowCallActions((prev) => !prev)
    }
  }

  const [nowMs, setNowMs] = useState<number>(Date.now())
  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 15000)
    return () => clearInterval(interval)
  }, [])

  const isCallActive = useMemo(() => {
    if (!isPrepMode) return true

    const visitTimeStr = patientWithVisitInfo?.VisitTime
    if (!visitTimeStr) return true

    const visitStartMs = moment(visitTimeStr).valueOf()
    if (!Number.isFinite(visitStartMs)) return true
    const thresholdMs = visitStartMs - 5 * 60 * 1000
    return nowMs >= thresholdMs
  }, [nowMs, isPrepMode, patientWithVisitInfo?.VisitTime])
  return (
    <div className='absolute bottom-0 flex w-full items-center bg-transparent p-sp-2'>
      <div className={cn('flex w-full justify-between')}>
        <div className='flex items-center gap-sp-2'>
          <Button
            priority='tertiary'
            className={cn('bg-surface-default', isPrepMode && 'opacity-30')}
            isIcon
            onClick={toggleMic}
            disabled={isPrepMode}>
            {preferredAudioOn ?
              <MicrophoneIcon />
            : <MicrophoneSlashIcon />}
          </Button>

          <Button
            priority='tertiary'
            className={cn('bg-surface-default', (!isVideoCall || isPrepMode) && 'opacity-30')}
            isIcon
            onClick={toggleCam}
            disabled={!isVideoCall || isPrepMode}>
            {preferredVideoOn ?
              <VideoIcon />
            : <VideoSlashIcon />}
          </Button>
        </div>
        {isPrepMode ?
          <div className='h-max rounded-full bg-white'>
            <Button priority='primary' onClick={onStartCall} disabled={!isCallActive}>
              {VisitMethod === 'video' ?
                <VideoIcon />
              : <PhoneIcon />}{' '}
              Call
            </Button>
          </div>
        : (
          is('CONNECTED_CALL_ENDED', 'IDLE', 'PROVIDER_WAS_CONNECTED_BUT_PATIENT_CONNECTION_FAILED') &&
          participantConnectionStatuses.endedBy
        ) ?
          <Button priority='primary' theme='success' onClick={onCallAgain}>
            {patientWithVisitInfo.VisitMethod !== 'video' && <PhoneIcon />} Call again{' '}
            {patientWithVisitInfo.VisitMethod === 'video' && <ChevronDownIcon />}
          </Button>
        : is('PROVIDER_CONNECTED_AND_WAITING_FOR_PATIENT_CONNECTION', 'CALL_IS_ACTIVE', 'PROVIDER_CONNECTING') ?
          <Button onClick={endVisitCall} priority='primary' theme='danger'>
            <PhoneSlashIcon /> Hang up
          </Button>
        : <div />}
      </div>
      {showCallActions ?
        <>
          <button
            type='button'
            className='fixed inset-0 z-10 cursor-default bg-transparent'
            onClick={() => setShowCallActions(false)}
            aria-label='Close call actions'
          />
          <div className='absolute -bottom-[120px] right-sp-2 z-20 flex flex-col gap-sp-1 rounded-lg bg-white p-sp-2 shadow-lg'>
            <Button
              priority='tertiary'
              disabled={visitIsPhoneCallOnly}
              onClick={() => {
                onStartVisitClickInVisit({ isPhoneCall: false })
                setShowCallActions(false)
              }}>
              <VideoIcon />
              Video call
            </Button>
            <Button
              priority='tertiary'
              onClick={() => {
                onStartVisitClickInVisit({ isPhoneCall: true })
                setShowCallActions(false)
              }}>
              <PhoneIcon />
              Phone call
            </Button>
          </div>
        </>
      : null}
    </div>
  )
}
